import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateClass } from '@/features/classes/hooks';
import { useLearningSpaces } from '@/features/learning-spaces/hooks';
import { ApiError } from '@/lib/api/client';

/**
 * When opened from a Learning Space's own detail page, `fixedLearningSpaceId`
 * pre-selects and locks that learning space - a class always belongs to
 * exactly one (`createClassSchema`), so there is nothing else to choose in
 * that context. Opened from the standalone Classes list, the picker is
 * editable and lists the organization's own learning spaces (first page,
 * matching this v1's lack of a search/autocomplete affordance elsewhere).
 */
export function CreateClassDialog({
  open,
  onOpenChange,
  onCreated,
  fixedLearningSpaceId,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: () => void;
  readonly fixedLearningSpaceId?: string;
}): JSX.Element {
  const [learningSpaceId, setLearningSpaceId] = useState(fixedLearningSpaceId ?? '');
  const [name, setName] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const create = useCreateClass();
  const { data: learningSpaces } = useLearningSpaces(1, 100);

  useEffect(() => {
    if (open) {
      setLearningSpaceId(fixedLearningSpaceId ?? '');
      setName('');
      setAcademicYear('');
      setValidationError(null);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixedLearningSpaceId]);

  function handleOpenChange(next: boolean): void {
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (learningSpaceId.length === 0) {
      setValidationError('Pilih ruang belajar.');
      return;
    }
    if (name.trim().length === 0) {
      setValidationError('Nama kelas tidak boleh kosong.');
      return;
    }

    create.mutate(
      {
        learningSpaceId,
        name: name.trim(),
        academicYear: academicYear.trim().length > 0 ? academicYear.trim() : null,
      },
      {
        onSuccess: () => {
          onCreated();
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tambah Kelas</DialogTitle>
          <DialogDescription>
            Kelas ini akan menampung santri dan guru yang diampu.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="class-learning-space">Ruang Belajar</Label>
            <Select
              value={learningSpaceId}
              onValueChange={setLearningSpaceId}
              disabled={fixedLearningSpaceId !== undefined}
            >
              <SelectTrigger id="class-learning-space">
                <SelectValue placeholder="Pilih ruang belajar" />
              </SelectTrigger>
              <SelectContent>
                {learningSpaces?.items.map((space) => (
                  <SelectItem key={space.id} value={space.id}>
                    {space.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="class-name">Nama Kelas</Label>
            <Input
              id="class-name"
              type="text"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="class-academic-year">Tahun Ajaran (opsional)</Label>
            <Input
              id="class-academic-year"
              type="text"
              value={academicYear}
              onChange={(event) => {
                setAcademicYear(event.target.value);
              }}
              placeholder="2026/2027"
              autoComplete="off"
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
